<script lang="ts">
	import {
		DefaultSubstituteMatrixData,
		type DirrtyScriptErrorData,
		MatchType,
		type SubstituteData,
		type SubstituteMatrixData,
		type SubstituteType
	} from './types/substitutes'
	import { onMount, setContext, untrack } from 'svelte'
	import { SvelteMap } from 'svelte/reactivity'
	import { createTippy } from 'svelte-tippy'
	import { fetchUtils } from '$utils/fetchUtils'
	import { escapeHtml } from '$utils/utils.svelte'
	import AutocompleteInputField from '#lib/autocomplete/AutocompleteInputField.svelte'
	import { AutocompleteConfig, type AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { DefaultInputFieldProps, type FontProps } from '$types/fontProps'
	import { t } from '$i18n/i18n'
	import type { Material } from './types/material'
	import { writable, type Writable } from 'svelte/store'
	import { mmpvState } from './mmpv.state.svelte'
	import HeadlessTable from '#lib/organisms/HeadlessTable.svelte'
	import { createRender } from 'svelte-headless-table'
	import HtmlCell from '#lib/headlessTableCells/HtmlCell.svelte'
	import { createCol } from '$types/headlessTable'

	setContext('useMaterialDesign', true)

	type MaterialSubstitutesProps = {
		substituteType?: SubstituteType
	}

	let { substituteType = $bindable() }: MaterialSubstitutesProps = $props()

	// wholesale reassignment on fetch, so $state.raw suffices for reactivity
	let substituteMatrixData: SubstituteMatrixData = $state.raw(DefaultSubstituteMatrixData())
	let substituteTypeId: string | undefined = $state()
	let substituteTypes: SubstituteType[] = $state([])
	// unmounts/remounts the table so the use:updateSubstituteMatchDataInTable action re-runs after a fetch
	let showSubstituteTableData: boolean = $state(true)

	const fontProps: FontProps = DefaultInputFieldProps()

	const SUBSTITUTE_FORM_ERROR_PATH_PREFIX = 'substitute.'
	let substituteTypeOptions = $derived(
		substituteTypes.map((type) => ({ text: type.name, value: type.publicId }))
	)

	let typeAcConfig: AutocompleteFieldConfig = AutocompleteConfig('value', 'text')

	let allNodes: SvelteMap<string, Material> = $derived(
		new SvelteMap(
			mmpvState.allExtendedMaterials.map((material): [string, Material] => [material.id, material])
		)
	)
	/**
	 * both maps are built in one pass over allNodes, so they are derived together
	 * and split into allVisibleNodes/allNodeNames below.
	 */
	let visibleNodesData: {
		visibleNodes: SvelteMap<string, string>
		nodeNames: SvelteMap<string, string>
	} = $derived.by(() => {
		const visibleNodes: SvelteMap<string, string> = new SvelteMap()
		const nodeNames: SvelteMap<string, string> = new SvelteMap()

		for (let [key, value] of allNodes) {
			// build table only if there are substitutes
			if (substituteMatrixData.substitutes.has(key)) {
				substituteMatrixData.substitutes.get(key)?.forEach((substituteData: SubstituteData) => {
					// candidate materials are expected in allNodes; fall back to an empty label instead of concatenating undefined
					const candidateMaterial = allNodes.get(substituteData.candidateMaterialId)
					visibleNodes.set(
						substituteData.candidateMaterialId,
						candidateMaterial ? candidateMaterial.parentCode + candidateMaterial.code : ''
					)
					nodeNames.set(substituteData.candidateMaterialId, substituteData.candidateMaterial.name)
				})
				visibleNodes.set(key, value.parentCode + value.code)
				let substituteData = substituteMatrixData.substitutes.get(key)?.[0]

				if (substituteData) {
					nodeNames.set(substituteData.referenceMaterialId, substituteData.referenceMaterial.name)
				}
			}
		}
		return { visibleNodes, nodeNames }
	})
	let allVisibleNodes: SvelteMap<string, string> = $derived(visibleNodesData.visibleNodes)
	let allNodeNames: SvelteMap<string, string> = $derived(visibleNodesData.nodeNames)

	// table data
	const tableData: Writable<DirrtyScriptErrorData[]> = setContext('table-data', writable([]))
	const colData: Writable<unknown[]> = setContext('table-cols', writable([]))

	onMount(() => {
		void fetchSubstituteTypes()
	})

	function addTooltip(content: string, id: string) {
		let tippy = createTippy({
			interactive: true,
			placement: 'right',
			maxWidth: 4000,
			theme: 'mmttip',
			content: content,
			hideOnClick: false,
			delay: [0, 200],
			allowHTML: true,
			appendTo: () => document.body
		})

		/**
		 * svelte-tippy types the returned action as (element: HTMLElement) => TippyReturn, so resolve
		 * the id to its element instead of passing a selector string; for a single id both are
		 * equivalent, and a missing element simply attaches no tooltip (as before).
		 */
		const tooltipTarget = document.getElementById(id)
		if (tooltipTarget) {
			tippy(tooltipTarget)
		}
	}

	async function fetchSubstituteTypes() {
		await fetchUtils
			.get('/api/substitutes/types')
			.then((data) => {
				substituteTypes = data
				if (substituteTypes.length > 0) {
					substituteType = substituteTypes[0]
					substituteTypeId = substituteType.publicId
					// if lot type is present start fetching substitute data of selected lot type
					fetchSubstituteMatrixData()
				}
			})
			.catch(fetchUtils.catchErrorAndShowNotification())
	}

	async function fetchSubstituteMatrixData() {
		showSubstituteTableData = false
		if (!substituteTypeId) {
			return
		}
		substituteType = substituteTypes.find((type) => type.publicId === substituteTypeId)

		await fetchUtils
			.get('/api/substitutes/matrix/' + substituteTypeId)
			.then((data) => {
				substituteMatrixData = {
					...DefaultSubstituteMatrixData(),
					...data,
					// substitutes arrives as a plain JSON object; convert so it can be iterated as a Map
					substitutes: new SvelteMap(Object.entries(data.substitutes))
				}
				showSubstituteTableData = true
			})
			.catch(fetchUtils.catchErrorAndShowNotification())
	}

	/**
	 * finds the row and column header cells rendered for the given material id.
	 * instanceof narrows the plain getElementById results to table cell/row types
	 * so cellIndex/rowIndex are type-safe.
	 */
	function findRowAndColumn(materialId: string, columnMaterialId: string) {
		const row = document.getElementById('row-' + materialId)
		const column = document.getElementById('col-' + columnMaterialId)
		if (row instanceof HTMLTableRowElement && column instanceof HTMLTableCellElement) {
			return { row, column }
		}
		return undefined
	}

	/**
	 * narrows a MatchType reverse lookup result so it can index the enum object again.
	 * the backend serializes MatchType as its name string, so substituteData.type holds a
	 * name at runtime despite its numeric declared type — the double lookup below
	 * (name -> ordinal -> name) normalizes it, and this predicate keeps that indexing type-safe.
	 */
	function isMatchTypeKey(value: string): value is keyof typeof MatchType {
		return value in MatchType
	}

	// svelte action: runs once the table element is mounted (remounted after each fetch)
	function updateSubstituteMatchDataInTable(table: HTMLTableElement) {
		// update nodes with same id in table by add match type match
		for (let key of allVisibleNodes.keys()) {
			const rowAndColumn = findRowAndColumn(key, key)
			const nodeName = allNodeNames.get(key)
			if (rowAndColumn && nodeName) {
				const { row, column } = rowAndColumn
				table.rows[row.rowIndex].cells[column.cellIndex].innerHTML = $t(
					'UI.MaterialData.substitutes.table.matchType.MATCH'
				)

				//set tooltip for material numbers
				addTooltip(escapeHtml(nodeName), table.rows[row.rowIndex].cells[0].id)
				addTooltip(escapeHtml(nodeName), table.rows[0].cells[column.cellIndex].id)
			}
		}
		// iterate through substitute data and update substitutes with tooltip and match type tag
		for (let [key, value] of substituteMatrixData.substitutes) {
			// then iterate through array list of substituteData
			for (let substituteData of value) {
				// for each substitute data find row of substitute map id and col of substitute match id
				const rowAndColumn = findRowAndColumn(key, substituteData.candidateMaterialId)
				if (rowAndColumn) {
					const { row, column } = rowAndColumn
					const cell = table.rows[row.rowIndex].cells[column.cellIndex]
					// backend sends name string; guard numeric ordinal so ordinal 0 → "MATCH" not "0"
					const rawType = substituteData.type as unknown
					const matchTypeName =
						typeof rawType === 'number' ? MatchType[rawType as number] : String(rawType)
					cell.innerHTML = $t(`UI.MaterialData.substitutes.table.matchType.${matchTypeName}`)
					cell.id =
						'match-ref-' + substituteData.candidateMaterialId + substituteData.referenceMaterialId
					// escape each field value individually so commas within values are not
					// mistaken for entry separators; join with the intentional ', <br>' separator
					addTooltip(
						Object.entries(substituteData as Record<string, unknown>)
							.map(([k, v]) => `${escapeHtml(k)}: ${escapeHtml(JSON.stringify(v))}`)
							.join(', <br>'),
						cell.id
					)
				}
			}
		}
	}

	const tablePlugins = {
		sort: { disable: true }
	}

	type ScriptErrorCell = { row: { original: DirrtyScriptErrorData } }

	$colData.push(
		createCol('Substitute ID', 'publicId', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.publicId
				})
		})
	)
	$colData.push(
		createCol('Typ Name', 'typeName', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.typeName
				})
		})
	)
	$colData.push(
		createCol('Referenz Katalog', 'referenceMaterialCatalogName', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.referenceMaterialCatalogName
				})
		})
	)
	$colData.push(
		createCol('Referenz Material', 'referenceMaterialName', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.referenceMaterialName
				})
		})
	)
	$colData.push(
		createCol('Kandidat Katalog', 'candidateMaterialCatalogName', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.candidateMaterialCatalogName
				})
		})
	)
	$colData.push(
		createCol('Kandidat Material', 'candidateMaterialName', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.candidateMaterialName
				})
		})
	)
	$colData.push(
		createCol('Fehlermeldung', 'errorMessage', {
			plugins: tablePlugins,
			cell: ({ row }: ScriptErrorCell) =>
				createRender(HtmlCell, {
					text: row.original.errorMessage
				})
		})
	)

	function transformScriptErrors() {
		if (mmpvState.scriptErrors != null) {
			$tableData = mmpvState.scriptErrors
		}
	}

	/**
	 * gates the on-mount run of the scriptErrors effect.
	 *
	 * on mount the outgoing (tall) view is still present during the tab fly-transition, so
	 * scrolling #script-error into view smooth-scrolls the whole page down; when that view is
	 * then removed the page collapses and the scroll snaps back — a visible up/down bounce.
	 * we only scroll for errors produced after mount (i.e. from "Skripte überprüfen").
	 */
	let scriptErrorsSettled = false

	/**
	 * untrack: the legacy `$:` statement only depended on the store value itself.
	 * The handler writes $tableData — tracking that read would re-fire the effect
	 * on its own write.
	 */
	$effect(() => {
		void mmpvState.scriptErrors
		untrack(() => {
			transformScriptErrors()
			if (scriptErrorsSettled && (mmpvState.scriptErrors?.length ?? 0) > 0) {
				document.getElementById('script-error')?.scrollIntoView({ behavior: 'smooth' })
			}
			scriptErrorsSettled = true
		})
	})
</script>

<div class="substitutes-container">
	<div style="width: 20rem; padding-bottom: 2rem">
		<AutocompleteInputField
			bind:value={substituteTypeId}
			{fontProps}
			placeholder="Substitut Typen"
			options={substituteTypeOptions}
			autocompleteConfig={typeAcConfig}
			hideDropdownArrow={false}
			errorPath={SUBSTITUTE_FORM_ERROR_PATH_PREFIX + 'type'}
			onChange={fetchSubstituteMatrixData}
		/>
	</div>

	<div class="wrapper-substitute-table scrollable">
		{#if allVisibleNodes?.size > 0 && substituteType && showSubstituteTableData}
			<table id="substitute-table" use:updateSubstituteMatchDataInTable>
				<thead>
					<tr>
						<td></td>
						{#each [...allVisibleNodes.keys()] as nodeId (nodeId)}
							<th scope="col" id={'col-' + nodeId}>{allVisibleNodes.get(nodeId)}</th>
						{/each}
					</tr>
				</thead>
				<tbody>
					{#each [...allVisibleNodes.keys()] as nodeId (nodeId)}
						<tr id={'row-' + nodeId} class="substitute-rows">
							<th id={'row-headercell-' + nodeId} scope="row">{allVisibleNodes.get(nodeId)}</th>

							{#each [...allVisibleNodes.keys()] as colNodeId (colNodeId)}
								<td>-</td>
							{/each}
						</tr>
					{/each}
				</tbody>
			</table>
		{/if}
	</div>
	<div id="script-error" class="script-error-main-container">
		{#if mmpvState.scriptErrors && mmpvState.scriptErrors.length > 0}
			<div class="script-error-table" class:emptyTable={$tableData.length === 0}>
				<HeadlessTable emptyMessage="Keine Fehler vorhanden"></HeadlessTable>
			</div>
		{/if}
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.substitutes-container {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: flex-start;

		width: 100%;
	}

	table,
	th,
	td {
		font-family: Roboto, sans-serif;
		line-height: 8px;
		font-weight: 400;
		font-size: 0.75rem;
		color: black;

		border: 0.15rem solid black;
		border-collapse: collapse;
	}

	td {
		min-width: 3rem;
		height: 3rem;

		font-family: Roboto, sans-serif;
		line-height: 8px;
		font-weight: 500;
		font-size: 0.8rem;
		color: black;
	}

	th {
		padding: 0 0.2rem 0 0.2rem;
	}

	.wrapper-substitute-table {
		width: fit-content;
		max-width: 100%;
		aspect-ratio: 1;
		overflow: auto;
		display: flex;
		padding: 2rem;
		border: 1px solid grey;
	}

	.scrollable::-webkit-scrollbar {
		width: 4px;
	}

	/* Track */
	.scrollable::-webkit-scrollbar-track {
		background: #f0f2f8;
		border-radius: 4px;
		margin-top: 1px;
		margin-bottom: 1px;
		margin-right: 12px;
	}

	/* Handle */
	.scrollable::-webkit-scrollbar-thumb {
		background: var(--color-green-500);
		border-radius: 4px;
	}

	.script-error-main-container {
		margin-top: 2rem;
	}
</style>
